/**
 * Shipping News Feed Widget
 * 
 * Displays latest shipping industry news headlines
 * from various sources like TradeWinds, Lloyd's List, etc.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, ExternalLink, RefreshCw, Clock } from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';

interface NewsItem {
  id: string;
  title: string;
  source: string;
  url: string;
  timestamp: Date;
  category: 'market' | 'ports' | 'vessels' | 'regulation' | 'general';
}

// Simulated news data - In production, use RSS feeds or news APIs
const generateNews = (): NewsItem[] => {
  const newsTemplates = [
    {
      title: 'Baltic Dry Index climbs for fifth consecutive session on Capesize gains',
      source: 'Lloyd\'s List',
      category: 'market' as const,
      url: 'https://lloydslist.com/LL1149457/Baltic-Dry-Index',
    },
    {
      title: 'China iron ore imports surge to 6-month high amid steel demand',
      source: 'TradeWinds',
      category: 'market' as const,
      url: 'https://www.tradewindsnews.com/dry-cargo/',
    },
    {
      title: 'Panama Canal eases transit restrictions as water levels recover',
      source: 'Splash247',
      category: 'ports' as const,
      url: 'https://splash247.com/category/sector/dry-cargo/',
    },
    {
      title: 'Dry bulk freight rates expected to remain firm through Q1 2026',
      source: 'Hellenic Shipping News',
      category: 'market' as const,
      url: 'https://www.hellenicshippingnews.com/category/dry-θαλasσ/',
    },
    {
      title: 'New IMO regulations on carbon intensity to impact older vessels',
      source: 'ShipInsight',
      category: 'regulation' as const,
      url: 'https://shipinsight.com/',
    },
    {
      title: 'Brazilian grain exports set new monthly record in December',
      source: 'TradeWinds',
      category: 'market' as const,
      url: 'https://www.tradewindsnews.com/dry-cargo/',
    },
    {
      title: 'Port of Rotterdam reports 8% increase in dry bulk throughput',
      source: 'Port Technology',
      category: 'ports' as const,
      url: 'https://www.porttechnology.org/news/category/dry-bulk/',
    },
    {
      title: 'Supramax rates strengthen on South American grain rush',
      source: 'Lloyd\'s List',
      category: 'market' as const,
      url: 'https://lloydslist.com/markets/dry-bulk',
    },
  ];

  // Randomize and pick 6 items
  const shuffled = [...newsTemplates].sort(() => Math.random() - 0.5);
  const selected = shuffled.slice(0, 6);

  return selected.map((item, index) => ({
    id: `news-${index}`,
    title: item.title,
    source: item.source,
    url: item.url,
    timestamp: new Date(Date.now() - index * 3600000 * (1 + Math.random() * 2)),
    category: item.category,
  }));
};

const categoryColors = {
  market: 'bg-ocean-cyan/20 text-ocean-cyan',
  ports: 'bg-ocean-purple/20 text-ocean-purple',
  vessels: 'bg-ocean-blue/20 text-ocean-blue',
  regulation: 'bg-amber-500/20 text-amber-400',
  general: 'bg-white/10 text-white/60',
};

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor(diff / 60000);

  if (hours >= 24) {
    return `${Math.floor(hours / 24)}d ago`;
  }
  if (hours >= 1) {
    return `${hours}h ago`;
  }
  return `${minutes}m ago`;
}

export function ShippingNews() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNews = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setNews(generateNews());
      setIsLoading(false);
    }, 600);
  };

  useEffect(() => {
    fetchNews();
    // Refresh every 15 minutes
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <GlassPanel className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-ocean-purple/20">
            <Newspaper className="w-4 h-4 text-ocean-purple" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Shipping News</h3>
            <p className="text-xs text-white/40">Industry headlines</p>
          </div>
        </div>
        <button
          onClick={fetchNews}
          disabled={isLoading}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={cn('w-4 h-4 text-white/60', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* News List - Scrollable */}
      <div className="space-y-3 overflow-y-auto max-h-[320px] pr-1 scrollbar-thin">
        {news.map((item, index) => (
          <motion.a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all group"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm text-white font-medium line-clamp-2 group-hover:text-ocean-cyan transition-colors">
                  {item.title}
                </h4>
                <div className="flex items-center gap-3 mt-2">
                  <span className={cn(
                    'px-2 py-0.5 rounded text-[10px] font-medium uppercase',
                    categoryColors[item.category]
                  )}>
                    {item.category}
                  </span>
                  <span className="text-xs text-white/40">{item.source}</span>
                  <span className="flex items-center gap-1 text-xs text-white/30">
                    <Clock className="w-3 h-3" />
                    {formatTimeAgo(item.timestamp)}
                  </span>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-white/20 group-hover:text-ocean-cyan transition-colors flex-shrink-0 mt-1" />
            </div>
          </motion.a>
        ))}
      </div>

      {/* View All Link */}
      <div className="mt-4 pt-3 border-t border-white/10 text-center">
        <a
          href="https://www.tradewindsnews.com/dry-cargo/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-ocean-cyan hover:text-ocean-cyan/80 transition-colors"
        >
          View all shipping news →
        </a>
      </div>
    </GlassPanel>
  );
}

export default ShippingNews;

